import { AuthResponse, LoginCredentials, User, DashboardStats, ShippingOrder, ScanOutData, ScanOutRecord, PackagesResponse } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiService {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth - uses unified login endpoint
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async getProfile(): Promise<User> {
    // Try unified profile first, fallback to shipping-portal specific
    try {
      return await this.request<User>('/auth/profile');
    } catch {
      return this.request<User>('/shipping-portal/profile');
    }
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/shipping/dashboard');
  }

  // Queues
  async getReadyToShipQueue(params?: Record<string, string>): Promise<ShippingOrder[]> {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<ShippingOrder[]>(`/shipping/queue/ready${query}`);
  }

  async getWillCallQueue(): Promise<ShippingOrder[]> {
    return this.request<ShippingOrder[]>('/shipping/queue/will-call');
  }

  async getShippedOrders(params?: Record<string, string>): Promise<ShippingOrder[]> {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<ShippingOrder[]>(`/shipping/queue/shipped${query}`);
  }

  // Order Details
  async getOrderForShipping(id: string): Promise<ShippingOrder> {
    return this.request<ShippingOrder>(`/shipping/orders/${id}`);
  }

  // Actions
  async bookShipment(data: {
    order_id: string;
    carrier_name?: string;
    bol_number?: string;
    pro_number?: string;
  }): Promise<ShippingOrder> {
    return this.request<ShippingOrder>('/shipping/book', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async schedulePickup(data: {
    order_id: string;
    pickup_date: string;
    pickup_time?: string;
    pickup_notes?: string;
  }): Promise<ShippingOrder> {
    return this.request<ShippingOrder>('/shipping/schedule-pickup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markShipped(data: {
    order_id: string;
    tracking_number?: string;
    estimated_delivery?: string;
  }): Promise<ShippingOrder> {
    return this.request<ShippingOrder>('/shipping/mark-shipped', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markDelivered(data: {
    order_id: string;
    delivered_to?: string;
    delivered_at?: string;
  }): Promise<ShippingOrder> {
    return this.request<ShippingOrder>('/shipping/mark-delivered', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Will Call
  async notifyWillCallReady(orderId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/shipping/will-call/${orderId}/notify`, {
      method: 'POST',
    });
  }

  async markWillCallPickedUp(data: {
    order_id: string;
    picked_up_by: string;
  }): Promise<ShippingOrder> {
    return this.request<ShippingOrder>('/shipping/will-call/picked-up', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Reports
  async getShippingReport(params?: Record<string, string>): Promise<any[]> {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any[]>(`/shipping/reports/shipments${query}`);
  }

  async getCarrierSummary(fromDate?: string, toDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const query = params.toString() ? '?' + params.toString() : '';
    return this.request<any[]>(`/shipping/reports/carriers${query}`);
  }

  // ============================================
  // PACKAGE MANAGEMENT
  // ============================================

  async getPackages(orderId: string): Promise<PackagesResponse> {
    return this.request<PackagesResponse>(`/shipping/orders/${orderId}/packages`);
  }

  async setPackageCounts(orderId: string, items: Array<{ item_number: number; package_count: number }>): Promise<PackagesResponse> {
    return this.request<PackagesResponse>(`/shipping/orders/${orderId}/packages`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async generatePackageLabels(orderId: string, printerId?: string): Promise<{ labels_queued: number; label_ids: string[]; printer_id: string | null }> {
    return this.request(`/shipping/orders/${orderId}/packages/labels`, {
      method: 'POST',
      body: JSON.stringify({ printer_id: printerId }),
    });
  }

  async resetPackages(orderId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/shipping/orders/${orderId}/packages`, {
      method: 'DELETE',
    });
  }

  async scanInPackage(barcode: string): Promise<any> {
    return this.request('/shipping/scan-in', {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });
  }

  async manualScanIn(orderId: string, itemNumber: number, packageNumber: number): Promise<any> {
    return this.request(`/shipping/orders/${orderId}/scan-in/manual`, {
      method: 'POST',
      body: JSON.stringify({ item_number: itemNumber, package_number: packageNumber }),
    });
  }

  async getPrinters(): Promise<any[]> {
    return this.request<any[]>('/labels/printers');
  }

  // ============================================
  // SCAN-OUT VERIFICATION
  // ============================================

  async getScanOutData(orderId: string): Promise<ScanOutData> {
    return this.request<ScanOutData>(`/shipping/orders/${orderId}/scan-out-data`);
  }

  async saveScanOut(orderId: string, data: {
    scan_type: 'will_call' | 'freight';
    scanned_items: Array<{ item_number: number; verified_at: string; method: 'scan' | 'manual' }>;
    accessories_confirmed: boolean;
    signature_data?: string;
    signed_by?: string;
    picked_up_by?: string;
    notes?: string;
  }): Promise<ScanOutRecord> {
    return this.request<ScanOutRecord>(`/shipping/orders/${orderId}/scan-out`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getScanOut(orderId: string): Promise<ScanOutRecord | null> {
    return this.request<ScanOutRecord | null>(`/shipping/orders/${orderId}/scan-out`);
  }

  // ============================================
  // NEW FREIGHT/FREIGHTVIEW METHODS
  // ============================================

  // Get orders ready to ship (with freight data)
  async getFreightReadyToShip(): Promise<any[]> {
    return this.request<any[]>('/freight/ready-to-ship');
  }

  // Get shipped orders (with BOL/PRO data)
  async getFreightShipped(): Promise<any[]> {
    return this.request<any[]>('/freight/shipped');
  }

  // Book shipment with FreightView (one-click)
  async bookWithFreightview(data: {
    freightviewShipmentId: string;
    quoteId: string;
    orderId: string;
  }): Promise<{
    success: boolean;
    bolNumber?: string;
    proNumber?: string;
    pickupConfirmation?: string;
    error?: string;
  }> {
    return this.request('/freight/book-freightview', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get shipping markup setting
  async getShippingMarkup(): Promise<{ shippingMarkupPercent: number }> {
    return this.request('/freight/shipping-markup');
  }

  // ============================================
  // FREIGHTVIEW SHIPMENT MANAGEMENT
  // ============================================

  async searchFVShipments(params?: Record<string, string>): Promise<any[]> {
    const query = params ? '?' + new URLSearchParams({ source: 'local', ...params }).toString() : '?source=local';
    return this.request<any[]>(`/freight/fv/shipments${query}`);
  }

  async getFVShipmentDetail(fvId: string): Promise<any> {
    return this.request(`/freight/fv/shipments/${fvId}`);
  }

  async getFVShipmentQuotes(fvId: string): Promise<any[]> {
    return this.request<any[]>(`/freight/fv/shipments/${fvId}/quotes`);
  }

  async getFVShipmentTracking(fvId: string): Promise<any[]> {
    return this.request<any[]>(`/freight/fv/shipments/${fvId}/tracking`);
  }

  async editFVShipment(fvId: string, data: any): Promise<any> {
    return this.request(`/freight/fv/shipments/${fvId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async cancelFVShipment(fvId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/freight/fv/shipments/${fvId}`, {
      method: 'DELETE',
    });
  }

  async createFVShipment(data: any): Promise<any> {
    return this.request('/freight/fv/shipments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async syncOrderTracking(orderId: string): Promise<{ success: boolean; status?: string }> {
    return this.request(`/freight/fv/sync/${orderId}`, {
      method: 'POST',
    });
  }

  async getFVCarriers(): Promise<any[]> {
    return this.request<any[]>('/freight/fv/carriers');
  }

  async calcShippingDims(orderId: string): Promise<any> {
    return this.request(`/freight/fv/calc-dims/${orderId}`);
  }

  async lookupOrder(orderNumber: string): Promise<any> {
    return this.request(`/freight/fv/lookup-order/${encodeURIComponent(orderNumber)}`);
  }

  // ============================================
  // PARCEL SHIPPING (UPS/FedEx/USPS)
  // ============================================

  async getPartsOrders(): Promise<any[]> {
    return this.request<any[]>('/shipping/queue/ready?order_type=parts');
  }

  async createParcelShipment(orderId: string): Promise<{
    success: boolean;
    shipmentId?: string;
    trackingNumber?: string;
    labelCount?: number;
    error?: string;
  }> {
    return this.request('/parcel/shipment', {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    });
  }

  async getParcelLabels(orderId: string): Promise<{
    success: boolean;
    trackingNumber?: string;
    labels?: Array<{ trackingNumber: string; labelFormat: string; labelBase64: string; packageIndex: number }>;
    error?: string;
  }> {
    return this.request(`/parcel/shipment/${orderId}/labels`);
  }

  // ============ Company Events APIs ============

  async getUpcomingEvents(limit: number = 5): Promise<CompanyEvent[]> {
    return this.request(`/company-events/upcoming?limit=${limit}`);
  }

  async getEmployeeEvents(startDate?: string, endDate?: string): Promise<CompanyEvent[]> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return this.request(`/company-events/employee?${params.toString()}`);
  }

  async getHolidays(year?: number): Promise<CompanyEvent[]> {
    const params = year ? `?year=${year}` : '';
    return this.request(`/company-events/holidays${params}`);
  }

  // ============================================
  // SHIPPING WORKSTATIONS
  // ============================================

  async getShippingWorkstations(): Promise<any[]> {
    return this.request<any[]>('/shipping/workstations');
  }

  async getShippingWorkstationQueue(workstationId: string): Promise<any[]> {
    return this.request<any[]>(`/shipping/workstations/${workstationId}/queue`);
  }

  async getShippingWorkCard(workOrderId: string): Promise<any> {
    return this.request<any>(`/shipping/work-orders/${workOrderId}/card`);
  }

  async startShippingWorkOrder(workOrderId: string): Promise<any> {
    return this.request<any>(`/shipping/work-orders/${workOrderId}/start`, { method: 'POST' });
  }

  async completeShippingWorkOrder(workOrderId: string): Promise<any> {
    return this.request<any>(`/shipping/work-orders/${workOrderId}/complete`, { method: 'POST' });
  }

  async skipShippingWorkOrder(workOrderId: string, reason?: string): Promise<any> {
    return this.request<any>(`/shipping/work-orders/${workOrderId}/skip`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // ============================================
  // PARTS BAG VERIFICATION
  // ============================================

  async verifyPartsBag(orderId: string, itemNumber: number): Promise<any> {
    return this.request<any>(`/shipping/orders/${orderId}/verify-parts-bag`, {
      method: 'POST',
      body: JSON.stringify({ itemNumber }),
    });
  }

  async verifyAllPartsBags(orderId: string): Promise<any> {
    return this.request<any>(`/shipping/orders/${orderId}/verify-all-parts-bags`, { method: 'POST' });
  }

  // ============================================
  // BAY MANAGEMENT
  // ============================================

  async getBays(): Promise<any[]> {
    return this.request<any[]>('/shipping/bays');
  }

  async assignBay(bayNumber: string, orderId: string): Promise<any> {
    return this.request<any>(`/shipping/bays/${bayNumber}/assign`, {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    });
  }

  async releaseBay(bayNumber: string): Promise<any> {
    return this.request<any>(`/shipping/bays/${bayNumber}/release`, { method: 'POST' });
  }

  // Consolidated shipments — group multiple orders for one dealer into one
  // physical shipment. Bound below the class as `api.consolidations.*`.
  consolidations = {
    list: (params?: { status?: string; dealerId?: string }): Promise<any[]> => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.dealerId) qs.set('dealerId', params.dealerId);
      return this.request<any[]>(`/shipments?${qs}`);
    },
    get: (id: string): Promise<any> => this.request<any>(`/shipments/${id}`),
    setQuote: (id: string, body: any): Promise<any> =>
      this.request<any>(`/shipments/${id}/quote`, { method: 'PATCH', body: JSON.stringify(body) }),
    book: (id: string, body: { bolNumber?: string; proNumber?: string; pickupConfirmation?: string }): Promise<any> =>
      this.request<any>(`/shipments/${id}/book`, { method: 'POST', body: JSON.stringify(body || {}) }),
    markShipped: (id: string, body: { trackingNumber?: string }): Promise<any> =>
      this.request<any>(`/shipments/${id}/mark-shipped`, { method: 'POST', body: JSON.stringify(body || {}) }),
    cancel: (id: string): Promise<any> =>
      this.request<any>(`/shipments/${id}/cancel`, { method: 'POST' }),
    setBoxes: (id: string, boxes: Array<{ length: number; width: number; height: number; weight: number }>): Promise<any> =>
      this.request<any>(`/shipments/${id}/boxes`, { method: 'PUT', body: JSON.stringify({ boxes }) }),
    // Status-agnostic edit for BOL/PRO/pickup/tracking — fix typos after the fact.
    updateShippingDetails: (
      id: string,
      body: { bolNumber?: string | null; proNumber?: string | null; pickupConfirmation?: string | null; trackingNumber?: string | null },
    ): Promise<any> =>
      this.request<any>(`/shipments/${id}/shipping-details`, { method: 'PATCH', body: JSON.stringify(body || {}) }),
  };
}

// Company Events
export interface CompanyEvent {
  id: string;
  title: string;
  description: string | null;
  eventType: string;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  location: string | null;
  virtualLink: string | null;
  visibility: string;
  department: string | null;
  status: string;
  color: string | null;
}

export const api = new ApiService();
export default api;
